import React from 'react'
import PropTypes from 'prop-types'
import {Cloudinary} from '@cloudinary/url-gen'
import {fill} from '@cloudinary/url-gen/actions/resize'
import {autoGravity} from '@cloudinary/url-gen/qualifiers/gravity'
import {Image} from '@salesforce/retail-react-app/app/components/shared/ui'

import {CLOUDINARY_CLOUD_NAME} from '../../constants'
import theme from '../../theme'

const extractPublicId = (url) => {
    try {
        const baseUrl = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/`
        if (!url?.startsWith(baseUrl)) {
            console.error('Invalid Cloudinary URL:', url)
            return ''
        }

        const parts = url.split('/upload/')

        if (parts.length < 2) return null // Ensure URL contains "/upload/"

        const afterUpload = parts[1] // Everything after "/upload/"

        // Split by "/" and remove transformation parameters
        const pathSegments = afterUpload.split('/')

        // Find the first segment that is **not** a transformation (i.e., the start of the public ID)
        let publicIdStartIndex = 0
        for (let i = 0; i < pathSegments.length; i++) {
            if (!pathSegments[i].includes('_') && !pathSegments[i].includes(',')) {
                publicIdStartIndex = i
                break
            }
        }

        // Rebuild the public ID from this point onward
        let publicId = pathSegments.slice(publicIdStartIndex).join('/')

        // Remove file extension if present
        publicId = publicId.replace(/\.[^/.]+$/, '')

        // Decode URI (to handle spaces and special characters correctly)
        return decodeURIComponent(publicId)
    } catch (error) {
        console.error('Error extracting publicId:', error)
        return ''
    }
}

const CloudinaryImage = ({
    src,
    lazyload = true,
    width,
    height,
    sizes,
    imgSizes,
    aspectRatio,
    styles = {},
    alt = ''
}) => {
    const publicId = extractPublicId(src)

    if (!publicId) {
        return null
    }

    const breakpoints = theme.breakpoints
    const cld = new Cloudinary({
        cloud: {cloudName: CLOUDINARY_CLOUD_NAME},
        url: {analytics: false}
    })
    const transformation = fill().gravity(autoGravity())

    if (width) {
        transformation.width(width)
    }

    if (height) {
        transformation.height(height)
    }

    if (aspectRatio) {
        transformation.aspectRatio(aspectRatio)
    }

    let sizeAttr = ''
    if (imgSizes) {
        Object.keys(imgSizes).map((key, index) => {
            sizeAttr += `
            (min-width: ${breakpoints[key]}) ${imgSizes[key]}${
                index < Object.keys(imgSizes).length - 1 ? ',' : ''
            }
            `
        })
    }

    let srcSetAttr = ''
    if (sizes?.length) {
        sizes?.map((w, index) => {
            const transformation = fill().gravity(autoGravity()).width(w)
            const transformationX2 = fill()
                .gravity(autoGravity())
                .width(w * 2)
            const transformationX3 = fill()
                .gravity(autoGravity())
                .width(w * 3)

            if (height) {
                const newHeight = (w * width) / parseFloat(height)
                transformation.height(newHeight)
                transformationX2.height(newHeight * 2)
                transformationX3.height(newHeight * 3)
            }

            if (aspectRatio) {
                transformation.aspectRatio(aspectRatio)
                transformationX2.aspectRatio(aspectRatio)
                transformationX3.aspectRatio(aspectRatio)
            }

            const img = cld
                .image(publicId)
                .resize(transformation)
                .format('auto')
                .quality('auto')
                .toURL()
            srcSetAttr += `
                ${img} ${w}w${index < sizes.length - 1 ? ',' : ''}
            `
        })
    }

    const image = cld.image(publicId).resize(transformation).format('auto').quality('auto').toURL()

    return (
        <Image
            src={image}
            className="cloudinary-image"
            sizes={sizeAttr}
            srcSet={srcSetAttr}
            alt={alt}
            {...(lazyload ? {loading: 'lazy'} : {fetchPriority: 'high', loading: 'eager'})}
            {...styles}
        />
    )
}

export const getCloudinaryImageUrl = ({src, width, height, aspectRatio}) => {
    const publicId = extractPublicId(src)

    if (!publicId) {
        return null
    }

    const cld = new Cloudinary({
        cloud: {cloudName: CLOUDINARY_CLOUD_NAME},
        url: {analytics: false}
    })
    const transformation = fill().gravity(autoGravity())

    if (width) {
        transformation.width(width)
    }

    if (height) {
        transformation.height(height)
    }

    if (aspectRatio) {
        transformation.aspectRatio(aspectRatio)
    }

    return cld.image(publicId).resize(transformation).format('auto').quality('auto').toURL()
}

CloudinaryImage.propTypes = {
    src: PropTypes.string.isRequired,
    lazyload: PropTypes.bool,
    height: PropTypes.number,
    width: PropTypes.number,
    sizes: PropTypes.array,
    imgSizes: PropTypes.object,
    aspectRatio: PropTypes.string,
    styles: PropTypes.object,
    alt: PropTypes.string
}

export default CloudinaryImage
